import { useCallback, useMemo, useState } from 'react';

import { ArrowRight, LoaderCircle } from 'lucide-react';
import { useLocation, useSearchParams, Link } from 'react-router-dom';

import { SidebarTrigger } from './Sidebar/Sidebar';
import { MatterType, RunStatus } from '../../../../backend/src/types/types';
import formatDate from '../../helpers/formatDate';
import WaitingApprovalScreen from '../../screens/Chronos/CaseEditor/components/CaseProcessing/WaitingApprovalScreen';
import Share, { ShareOptions } from '../../screens/Chronos/CaseEditor/components/Share';
import { AnnouncementBanner } from '../atoms/AnnouncementBanner';
import { NovuInbox } from '../molecules/NovuInbox';
import { useGetCase } from '@/api/queries/useGetCase';
import { useGetMatter } from '@/api/queries/useGetMatter';
import Modal from '@/components/molecules/Modals/SettingsModal';
import { isProcessingStatus } from '@/constants/pipelineStatus';

export const HeaderBar = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const caseId = searchParams.get('caseId');
  const matterId = searchParams.get('matterId');

  const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false);

  const { data: caseData } = useGetCase(caseId);
  const { data: matterData } = useGetMatter(matterId ?? caseData?.matterId ?? '');

  const handleOpenApprovalModal = useCallback(() => {
    setIsApprovalModalOpen(true);
  }, []);

  const handleCloseApprovalModal = useCallback(() => {
    setIsApprovalModalOpen(false);
  }, []);

  const isCaseFirstProcessing = Boolean(
    caseData?.runs?.at(0) &&
      isProcessingStatus(caseData.runs[0].status as RunStatus) &&
      caseData.runs.every((run) => run.status !== RunStatus.Complete),
  );

  const isCaseReprocessing = Boolean(
    caseData?.runs?.at(0) &&
      isProcessingStatus(caseData.runs[0].status as RunStatus) &&
      caseData.runs.length > 1 &&
      caseData.runs.some((run) => run.status === RunStatus.Complete),
  );

  const navigationInfo = useMemo(() => {
    const path = location.pathname;

    // Case Editor
    if (path.includes('/case-editor')) {
      if (caseData) {
        return {
          breadcrumbs: [
            {
              label: caseData.matter?.name || 'Matter',
              href: `/app/chronos/explore?matterId=${caseData.matterId}`,
            },
            { label: caseData.name, href: null },
          ],
          showBar: true,
        };
      }
      return { breadcrumbs: [], showBar: true };
    }

    // Cases Explorer
    if (path.includes('/explore')) {
      if (matterData) {
        return {
          breadcrumbs: [{ label: matterData.name, href: null }],
          showBar: true,
        };
      }
      return { breadcrumbs: [], showBar: true };
    }

    // All other pages - show bar but no breadcrumbs
    return {
      breadcrumbs: [{ label: 'Welcome', href: '/app/chronos/matters' }],
      showBar: true,
    };
  }, [location.pathname, caseData, matterData]);

  // Always show the top bar
  if (!navigationInfo.showBar) {
    return null;
  }

  return (
    <div>
      <AnnouncementBanner />

      <div className="h-13 min-h-13 w-full border-b bg-gray-50 pr-4 pl-4">
        <div className="flex h-full items-center justify-between">
          <div className="flex items-center gap-2">
            <SidebarTrigger />

            {/* Left side - Navigation breadcrumbs */}
            <div className="flex items-center gap-2">
              {navigationInfo.breadcrumbs.length > 0 && (
                <div className="flex items-center gap-2 text-sm">
                  {navigationInfo.breadcrumbs.map((breadcrumb, index) => (
                    <div key={breadcrumb.label} className="flex items-center gap-2">
                      {index > 0 && <span className="text-gray-400">/</span>}
                      {breadcrumb.href ? (
                        <Link to={breadcrumb.href} className="text-gray-600 hover:text-gray-900 hover:underline">
                          {breadcrumb.label.length > 50 ? breadcrumb.label.slice(0, 50) + '...' : breadcrumb.label}
                        </Link>
                      ) : (
                        <span className="font-medium text-gray-900" data-testid={`breadcrumb-${index}`}>
                          {breadcrumb.label.length > 50 ? breadcrumb.label.slice(0, 50) + '...' : breadcrumb.label}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right side - User info and actions */}
          <div className="flex items-center gap-4">
            {['/explore', '/case-editor'].some((segment) => location.pathname.includes(segment)) && (
              <>
                {isCaseReprocessing && (
                  <>
                    {caseData?.runs?.[0]?.status === RunStatus.AwaitingApproval ? (
                      <div
                        className="flex items-center gap-2 rounded-full border-2 border-blue-400 bg-blue-50 px-3 py-1 text-sm font-medium text-blue-700 shadow-sm transition-all hover:scale-105 hover:cursor-pointer hover:bg-blue-100 hover:shadow-md"
                        onClick={handleOpenApprovalModal}
                      >
                        <span>Check Page Estimate</span>
                        <ArrowRight className="h-3 w-3" />
                      </div>
                    ) : (
                      <div className="flex items-center gap-3 rounded-full border-2 border-amber-300 bg-amber-50 px-4 py-1 text-sm font-medium text-amber-800">
                        <div className="flex items-center gap-2">
                          <LoaderCircle className="h-4 w-4 animate-spin text-amber-600" />
                          <span className="font-semibold">Reprocessing...</span>
                        </div>
                        {caseData?.runs?.[0]?.completedAtEstimate && (
                          <div className="flex items-center gap-1 border-l border-amber-200 pl-3">
                            <span className="text-xs text-amber-700">
                              Estimated completion: {formatDate(caseData.runs[0].completedAtEstimate, true, true)}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </>
            )}

            <NovuInbox />

            {['/explore', '/case-editor'].some((segment) => location.pathname.includes(segment)) &&
              // Don't show Share button on /explore page for personal matters
              !(location.pathname.includes('/explore') && matterData?.type === MatterType.Personal) && (
                <Share
                  matterId={matterId ?? caseData?.matterId ?? undefined}
                  caseId={caseId ?? undefined}
                  allowedOptions={
                    matterData?.type === MatterType.General
                      ? [ShareOptions.MoveToDifferentMatter, ShareOptions.AddUsers]
                      : [ShareOptions.MoveToDifferentMatter]
                  }
                />
              )}
          </div>
        </div>
        <Modal
          title="Processing Approval Required"
          isOpen={isApprovalModalOpen}
          handleClose={handleCloseApprovalModal}
          content={
            caseData?.runs?.[0] && (
              <div className="px-6 py-8">
                <WaitingApprovalScreen
                  runId={caseData.runs[0].id}
                  pageCount={caseData.runs[0].totalPages}
                  durationEstimate={caseData.runs[0]?.durationEstimate}
                  onApprovalGranted={handleCloseApprovalModal}
                  onApprovalDenied={handleCloseApprovalModal}
                />
              </div>
            )
          }
        />
      </div>
    </div>
  );
};
